import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
  Logout,
  Person,
  Link as LinkFallback,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import * as LucideIcons from "lucide-react";
import { NotificationSidebarBadge } from "@/features/notifications";
import type { NotificationAreaCode } from "@/constants/api.notifications";
import { SidebarHeader } from "@/components/Layout/SidebarHeader";
import { getNavServiceColors } from "@/theme/theme";
import { isSidebarPathActive } from "@/shared/navigation/sidebarNavActive";
import { useSidebarNav } from "@/contexts/SidebarNavContext";
import type { SidebarNavItem } from "@/shared/sidebar/types";
import { API_URL } from "@/shared/config/api";
import { resolveMediaUrl } from "@/pages/Services/servicesCatalogUtils";
import { trackSidebarItemClick } from "@/shared/sidebar/trackSidebarClick";
import { useAuth } from "@/app/providers";

interface SidebarProps {
  onLogout: () => void;
  onNavigate: (path: string) => void;
  currentPath: string;
  collapsed?: boolean;
  onToggleSidebar?: () => void;
}

function resolveLucideIcon(iconName?: string): React.ComponentType<any> | null {
  if (!iconName) return null;
  const IconComponent = (LucideIcons as any)[iconName];
  if (
    typeof IconComponent === "function" ||
    (typeof IconComponent === "object" &&
      IconComponent &&
      "$$typeof" in IconComponent)
  ) {
    return IconComponent as React.ComponentType<any>;
  }
  return null;
}

const DynamicSidebar: React.FC<SidebarProps> = ({
  onLogout,
  onNavigate,
  currentPath,
  collapsed = false,
  onToggleSidebar,
}) => {
  void onToggleSidebar;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { config, loading } = useSidebarNav();
  const { token, currentBuilding } = useAuth();
  const isCompactSidebar = useMediaQuery("(max-width:1400px)");
  const isUltraCompactSidebar = useMediaQuery("(max-width:1200px)");
  const isShortViewport = useMediaQuery("(max-height:860px)");
  const compact = isCompactSidebar || isShortViewport;
  const ultraCompact = isUltraCompactSidebar || isShortViewport;

  const serviceColors = getNavServiceColors(
    theme.palette.primary.main,
    theme.palette.secondary.main,
  );

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState("");
  const [linksOpen, setLinksOpen] = useState(false);
  const [linksTitle, setLinksTitle] = useState("");
  const [links, setLinks] = useState<
    { label: string; url?: string; path?: string; icon?: string }[]
  >([]);
  /** Same pattern as old CooperativeU `reservationsOpen` — open by default. */
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );

  // Load PDF as a blob URL so local Django X-Frame-Options / 404 HTML never
  // break the iframe (cross-origin embed of /media/ is fragile in demo).
  useEffect(() => {
    if (!pdfOpen || !pdfUrl) {
      setPdfLoading(false);
      setPdfError(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfLoading(true);
    setPdfError(null);
    setPdfObjectUrl(null);

    const load = async () => {
      try {
        const res = await fetch(pdfUrl, { credentials: "omit" });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfObjectUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setPdfError(t("common.loadError"));
          // S3 / CDN: direct iframe often works; localhost /media 404 must not.
          if (
            /^https:\/\//i.test(pdfUrl) &&
            !/localhost|127\.0\.0\.1/i.test(pdfUrl)
          ) {
            setPdfObjectUrl(pdfUrl);
          }
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    };
    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [pdfOpen, pdfUrl, t]);

  const groupContainsActivePath = (item: SidebarNavItem): boolean =>
    (item.children || []).some((child) => {
      if (child.type === "page" && child.path) {
        return isSidebarPathActive(currentPath, child.path);
      }
      if (child.type === "group") return groupContainsActivePath(child);
      return false;
    });

  const isGroupExpanded = (item: SidebarNavItem, groupKey: string) => {
    if (Object.prototype.hasOwnProperty.call(expandedGroups, groupKey)) {
      return expandedGroups[groupKey];
    }
    // Active route inside the folder always reveals children once.
    if (groupContainsActivePath(item)) return true;
    return item.defaultExpanded !== false;
  };

  const toggleGroup = (item: SidebarNavItem, groupKey: string) => {
    setExpandedGroups((prev) => {
      const currently = Object.prototype.hasOwnProperty.call(prev, groupKey)
        ? prev[groupKey]
        : groupContainsActivePath(item) || item.defaultExpanded !== false;
      return {
        ...prev,
        [groupKey]: !currently,
      };
    });
  };

  const resolveColor = (item: SidebarNavItem) => {
    if (item.color) return item.color;
    const token = item.colorToken || "";
    return (serviceColors as Record<string, string>)[token] || theme.palette.primary.main;
  };

  const labelOf = (item: SidebarNavItem) => {
    const labels = item.labels;
    const lang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
    const other = lang === "en" ? "fr" : "en";
    // Custom labels win. If EN is empty, keep FR (do not jump to catalog i18n).
    if (labels?.[lang]?.trim()) return labels[lang]!.trim();
    if (labels?.[other]?.trim()) return labels[other]!.trim();
    if (item.labelKey) {
      const translated = t(item.labelKey);
      if (translated?.trim() && translated !== item.labelKey) return translated.trim();
    }
    return item.pageKey || "";
  };

  const handleItemClick = (item: SidebarNavItem) => {
    trackSidebarItemClick(
      token,
      { ...item, label: labelOf(item) },
      currentBuilding?.pk ? Number(currentBuilding.pk) : null,
    );
    if (item.type === "page" && item.path) {
      onNavigate(item.path);
      return;
    }
    if (item.type === "external" && item.url) {
      if (item.openInNewTab === false) {
        window.location.href = item.url;
      } else {
        window.open(item.url, "_blank", "noopener,noreferrer");
      }
      return;
    }
    if (item.type === "modal") {
      const title = labelOf(item);
      if (item.modalKind === "pdf") {
        setPdfTitle(title);
        const raw =
          item.pdfUrl || item.payload?.url || null;
        setPdfUrl(resolveMediaUrl(raw, API_URL));
        setPdfOpen(true);
        return;
      }
      if (item.modalKind === "links") {
        setLinks(
          (item.payload?.links || []).map((link) => {
            let path: string | undefined;
            if (link.pageKey) {
              path = findPagePath(config.items, link.pageKey);
            }
            return {
              label: link.labelKey
                ? t(link.labelKey)
                : link.url || link.pageKey || "",
              url: link.url,
              path,
              icon: link.icon,
            };
          }),
        );
        setLinksTitle(title);
        setLinksOpen(true);
      }
    }
  };

  const renderIcon = (item: SidebarNavItem, isActive: boolean, color: string) => {
    const Lucide = resolveLucideIcon(item.icon);
    if (Lucide) {
      return (
        <Lucide
          size={ultraCompact ? 16 : compact ? 18 : 22}
          color={isActive ? color : "currentColor"}
          strokeWidth={2}
        />
      );
    }
    return (
      <LinkFallback
        sx={{
          color: isActive ? color : "inherit",
          fontSize: ultraCompact ? "1rem" : compact ? "1.1rem" : { xs: "1.2rem", sm: "1.5rem" },
        }}
      />
    );
  };

  const itemSx = (isActive: boolean, color: string) => ({
    borderRadius: { xs: 1.5, sm: 2 },
    mb: ultraCompact ? 0.15 : { xs: 0.25, sm: 0.5 },
    cursor: "pointer",
    bgcolor: isActive ? `${color}20` : "transparent",
    border: isActive ? `1px solid ${color}40` : "1px solid transparent",
    "&:hover": { bgcolor: `${color}10`, border: `1px solid ${color}30` },
    transition: "all 0.2s ease-in-out",
    minWidth: 0,
    overflow: "hidden",
    py: ultraCompact ? 0.3 : compact ? 0.5 : { xs: 0.75, sm: 1 },
  });

  const renderNavItem = (item: SidebarNavItem, key: string) => {
    if (item.type === "divider") {
      return (
        <Divider
          key={key}
          sx={{ my: ultraCompact ? 0.5 : 1, borderColor: theme.palette.divider }}
        />
      );
    }
    if (item.type === "group") {
      const groupKey = String(item.id ?? key);
      const isExpanded = isGroupExpanded(item, groupKey);
      const anyChildActive = groupContainsActivePath(item);
      const color = resolveColor(item);

      return (
        <React.Fragment key={key}>
          <ListItem
            onClick={() => toggleGroup(item, groupKey)}
            sx={{
              borderRadius: { xs: 1.5, sm: 2 },
              mb: ultraCompact ? 0.15 : { xs: 0.25, sm: 0.5 },
              cursor: "pointer",
              bgcolor: anyChildActive ? `${color}14` : "transparent",
              border: anyChildActive
                ? `1px solid ${color}2a`
                : "1px solid transparent",
              "&:hover": {
                bgcolor: `${color}10`,
                border: `1px solid ${color}30`,
              },
              transition: "all 0.2s ease-in-out",
              minWidth: 0,
              overflow: "hidden",
              py: ultraCompact ? 0.3 : compact ? 0.5 : { xs: 0.75, sm: 1 },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: ultraCompact
                  ? "1.8rem"
                  : compact
                    ? "2rem"
                    : { xs: "2.25rem", sm: "3rem" },
              }}
            >
              {renderIcon(item, anyChildActive, color)}
            </ListItemIcon>
            <ListItemText
              primary={labelOf(item)}
              sx={{
                "& .MuiListItemText-primary": {
                  color: anyChildActive ? color : "inherit",
                  fontWeight: anyChildActive ? 600 : 500,
                  fontSize: ultraCompact
                    ? "0.78rem"
                    : compact
                      ? "0.84rem"
                      : { xs: "var(--fs-sm)", sm: "var(--fs-md)" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
            {!collapsed ? (isExpanded ? <ExpandLess /> : <ExpandMore />) : null}
          </ListItem>

          <Collapse in={collapsed || isExpanded} timeout="auto" unmountOnExit>
            <List
              disablePadding
              sx={{ pl: collapsed ? 0 : ultraCompact ? 1.5 : { xs: 2, sm: 3 } }}
            >
              {(item.children || []).map((child, idx) =>
                renderNavItem(child, `${key}-c-${idx}`),
              )}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    const color = resolveColor(item);
    const isActive =
      item.type === "page" && item.path
        ? isSidebarPathActive(currentPath, item.path)
        : false;
    const section = (item.notificationSection || undefined) as
      | NotificationAreaCode
      | undefined;

    return (
      <ListItem
        key={key}
        onClick={() => handleItemClick(item)}
        sx={itemSx(isActive, color)}
      >
        <ListItemIcon
          sx={{
            minWidth: ultraCompact ? "1.8rem" : compact ? "2rem" : { xs: "2.25rem", sm: "3rem" },
          }}
        >
          <NotificationSidebarBadge section={section}>
            {renderIcon(item, isActive, color)}
          </NotificationSidebarBadge>
        </ListItemIcon>
        <ListItemText
          primary={labelOf(item)}
          sx={{
            "& .MuiListItemText-primary": {
              color: isActive ? color : "inherit",
              fontWeight: isActive ? 600 : 400,
              fontSize: ultraCompact
                ? "0.78rem"
                : compact
                  ? "0.84rem"
                  : { xs: "var(--fs-sm)", sm: "var(--fs-md)" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        />
      </ListItem>
    );
  };

  const items = useMemo(() => config.items || [], [config.items]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <SidebarHeader
        collapsed={collapsed}
        onNavigateHome={() => onNavigate("/")}
        compact={compact}
        ultraCompact={ultraCompact}
      />

      <List
        sx={{
          px: collapsed ? 0.5 : ultraCompact ? 0.5 : { xs: 1, sm: 2 },
          py: ultraCompact ? 0.35 : { xs: 0.5, sm: 1 },
          flex: 1,
          overflow: "auto",
          width: "100%",
          boxSizing: "border-box",
          "& .MuiListItem-root": {
            minHeight: ultraCompact
              ? "2rem"
              : compact
                ? "2.35rem"
                : { xs: "2.75rem", sm: "3rem" },
            px: collapsed ? 1 : undefined,
            justifyContent: collapsed ? "center" : undefined,
          },
          "& .MuiListItemText-root": {
            display: collapsed ? "none" : "block",
          },
          "& .MuiListItemIcon-root": {
            minWidth: collapsed ? "auto" : undefined,
            justifyContent: collapsed ? "center" : undefined,
          },
        }}
      >
        {!loading
          ? items.map((item, index) => renderNavItem(item, `nav-${index}`))
          : null}
      </List>

      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          mt: "auto",
          pt: ultraCompact ? 0.75 : compact ? 1 : 1.25,
          overflow: "hidden",
        }}
      >
        <List
          sx={{
            px: collapsed ? 0.5 : ultraCompact ? 0.5 : { xs: 1, sm: 2 },
            py: ultraCompact ? 0.35 : { xs: 0.5, sm: 1 },
            width: "100%",
            boxSizing: "border-box",
            "& .MuiListItem-root": {
              px: collapsed ? 1 : undefined,
              justifyContent: collapsed ? "center" : undefined,
            },
            "& .MuiListItemText-root": {
              display: collapsed ? "none" : "block",
            },
            "& .MuiListItemIcon-root": {
              minWidth: collapsed ? "auto" : undefined,
              justifyContent: collapsed ? "center" : undefined,
            },
          }}
        >
          <ListItem
            onClick={() => onNavigate("/profile")}
            sx={itemSx(currentPath === "/profile", serviceColors.profile)}
          >
            <ListItemIcon
              sx={{
                minWidth: ultraCompact
                  ? "1.8rem"
                  : compact
                    ? "2rem"
                    : { xs: "2.25rem", sm: "3rem" },
              }}
            >
              <Person
                sx={{
                  color:
                    currentPath === "/profile"
                      ? serviceColors.profile
                      : "inherit",
                  fontSize: ultraCompact
                    ? "1rem"
                    : compact
                      ? "1.1rem"
                      : { xs: "1.2rem", sm: "1.5rem" },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={t("navigation.profile")}
              sx={{
                "& .MuiListItemText-primary": {
                  color:
                    currentPath === "/profile"
                      ? serviceColors.profile
                      : "inherit",
                  fontWeight: currentPath === "/profile" ? 600 : 400,
                  fontSize: ultraCompact
                    ? "0.78rem"
                    : compact
                      ? "0.84rem"
                      : { xs: "var(--fs-sm)", sm: "var(--fs-md)" },
                },
              }}
            />
          </ListItem>

          <ListItem
            onClick={onLogout}
            sx={{
              borderRadius: { xs: 1.5, sm: 2 },
              cursor: "pointer",
              "&:hover": {
                bgcolor: "error.light",
                color: "error.contrastText",
              },
              transition: "all 0.2s ease-in-out",
              py: ultraCompact ? 0.3 : compact ? 0.5 : { xs: 0.75, sm: 1 },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: ultraCompact
                  ? "1.8rem"
                  : compact
                    ? "2rem"
                    : { xs: "2.25rem", sm: "3rem" },
              }}
            >
              <Logout
                sx={{
                  fontSize: ultraCompact
                    ? "1rem"
                    : compact
                      ? "1.1rem"
                      : { xs: "1.2rem", sm: "1.5rem" },
                }}
              />
            </ListItemIcon>
            <ListItemText primary={t("auth.logout")} />
          </ListItem>
        </List>
      </Box>

      <Dialog
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {pdfTitle}
          <IconButton
            aria-label="close"
            onClick={() => setPdfOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ height: { xs: "70vh", md: "75vh" }, p: 0 }}>
          {pdfLoading ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <Typography variant="body2">{t("common.loading")}</Typography>
            </Box>
          ) : pdfError && !pdfObjectUrl ? (
            <Box sx={{ p: 3 }}>
              <Typography color="error">{pdfError}</Typography>
            </Box>
          ) : pdfObjectUrl ? (
            <Box
              component="iframe"
              src={pdfObjectUrl}
              title={pdfTitle}
              sx={{ width: "100%", height: "100%", border: 0 }}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          {pdfUrl ? (
            <Button
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("common.openInNewTab")}
            </Button>
          ) : null}
          <Button
            onClick={() => {
              setPdfOpen(false);
              setPdfUrl(null);
              setPdfObjectUrl(null);
              setPdfError(null);
            }}
          >
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linksOpen}
        onClose={() => setLinksOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {linksTitle}
          <IconButton
            aria-label="close"
            onClick={() => setLinksOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            {links.map((link, idx) => {
              const Lucide = resolveLucideIcon(link.icon);
              return (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box
                    onClick={() => {
                      if (link.path) {
                        setLinksOpen(false);
                        onNavigate(link.path);
                      } else if (link.url) {
                        window.open(link.url, "_blank", "noopener,noreferrer");
                      }
                    }}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      "&:hover": { bgcolor: "action.hover" },
                    }}
                  >
                    {Lucide ? <Lucide size={18} /> : <LinkFallback fontSize="small" />}
                    <Typography variant="body2">{link.label}</Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinksOpen(false)}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function findPagePath(items: SidebarNavItem[], pageKey: string): string | undefined {
  for (const item of items) {
    if (item.type === "page" && item.pageKey === pageKey && item.path) {
      return item.path;
    }
    if (item.type === "group" && item.children) {
      const nested = findPagePath(item.children, pageKey);
      if (nested) return nested;
    }
  }
  // Fallback catalog paths used when page is not in this sidebar but still Area-enabled via modal
  const FALLBACK: Record<string, string> = {
    home: "/",
    appointments: "/appointments",
    meetings: "/meetings",
    concierge: "/concierge",
    lending: "/lending",
    messaging: "/messagerie",
    services: "/services",
    bar: "/bar",
    events: "/events",
    gym: "/gym",
    beauty: "/beauty",
    external_links: "/external-links",
    ticketing: "/ticketing/report",
  };
  return FALLBACK[pageKey];
}

export default DynamicSidebar;
